import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Eye, Users, TrendingUp, Clock, ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const LIVE_WINDOW_MIN = 5;
const POLL_INTERVAL_MS = 5000;
const PAGE_SIZE = 50;

function parseUA(ua = "") {
  const browser =
    /Firefox\//.test(ua) ? "Firefox" :
    /Edg\//.test(ua) ? "Edge" :
    /Chrome\//.test(ua) ? "Chrome" :
    /Safari\//.test(ua) ? "Safari" :
    /Opera|OPR\//.test(ua) ? "Opera" :
    "Unknown";
  const os =
    /Windows NT 10/.test(ua) ? "Windows 10/11" :
    /Windows/.test(ua) ? "Windows" :
    /Mac OS X/.test(ua) ? "macOS" :
    /Android/.test(ua) ? "Android" :
    /iPhone|iPad|iPod/.test(ua) ? "iOS" :
    /Linux/.test(ua) ? "Linux" :
    "Unknown";
  const device = /Mobile|Android|iPhone|iPod/.test(ua) ? "Mobile" : /iPad|Tablet/.test(ua) ? "Tablet" : "Desktop";
  return { browser, os, device };
}

function timeAgo(iso) {
  if (!iso) return "-";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 5) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/** ISO-2 country code → emoji flag (regional indicator symbols). */
function countryFlag(code) {
  if (!code || code.length !== 2) return "";
  const cc = code.toUpperCase();
  const base = 0x1F1E6; // 'A'
  return String.fromCodePoint(base + cc.charCodeAt(0) - 65, base + cc.charCodeAt(1) - 65);
}

function StatTile({ label, value, sub, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="bg-[#0D0D0D] p-5">
      <div className="flex items-start justify-between mb-3">
        <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1]">{label}</p>
        <Icon className="h-4 w-4 text-[#39FF14]" strokeWidth={1.5} />
      </div>
      <p className="text-2xl sm:text-3xl font-black text-[#F2F2F2] mb-1" style={{ fontFamily: "Chivo, sans-serif" }}>
        {value}
      </p>
      {sub && <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">{sub}</p>}
    </div>
  );
}

function VisitorRow({ v, expanded, onToggle, showLiveBadge }) {
  const { browser, os, device } = parseUA(v.user_agent);
  const flag = v.geo?.country_code ? countryFlag(v.geo.country_code) : "";
  const location = v.geo ? [v.geo.city, v.geo.country].filter(Boolean).join(", ") : "";
  return (
    <div data-testid={`visitor-row-${v.id}`} className="border-b border-[#1A1A1A] hover:bg-[#0D0D0D] transition-colors">
      <button
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center gap-3 text-sm"
      >
        {showLiveBadge && (
          <span className="inline-block w-2 h-2 rounded-full bg-[#39FF14] animate-pulse shrink-0" title="Active" />
        )}
        <span className="font-mono text-[#39FF14] text-xs shrink-0 w-20">
          {timeAgo(v.last_seen || v.created_at)}
        </span>
        <span className="text-[#F2F2F2] truncate flex-1">{v.path}</span>
        <span className="text-[#A1A1A1] text-xs hidden sm:inline shrink-0">
          {v.user_email ? <span className="text-[#39FF14]">{v.user_email}</span> : "Anonymous"}
        </span>
        <span className="text-[#A1A1A1] font-mono text-[10px] hidden md:inline shrink-0 w-40 truncate" title={location}>
          {flag} {location || <span className="opacity-50">—</span>}
        </span>
        <span className="text-[#A1A1A1] font-mono text-[10px] hidden md:inline shrink-0">
          {browser} / {os} / {device}
        </span>
        <span className="text-[#A1A1A1] font-mono text-[10px] hidden lg:inline shrink-0 w-32 truncate">
          {v.ip || "-"}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-1 bg-[#050505] border-l-2 border-[#39FF14] ml-2" data-testid={`visitor-detail-${v.id}`}>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs font-mono">
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Visitor ID</dt><dd className="text-[#F2F2F2] truncate">{v.visitor_id}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Path</dt><dd className="text-[#F2F2F2] break-all">{v.path}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">First Ping</dt><dd className="text-[#F2F2F2]">{v.created_at ? new Date(v.created_at).toLocaleString() : "-"}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Last Ping</dt><dd className="text-[#F2F2F2]">{v.last_seen ? new Date(v.last_seen).toLocaleString() : "-"}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">IP</dt><dd className="text-[#F2F2F2]">{v.ip || "-"}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Location</dt><dd className="text-[#F2F2F2]">{v.geo ? `${flag} ${[v.geo.city, v.geo.region, v.geo.country].filter(Boolean).join(", ")}${v.geo.timezone ? ` (${v.geo.timezone})` : ""}` : <span className="opacity-60">Not resolved (private IP or lookup pending)</span>}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Referrer</dt><dd className="text-[#F2F2F2] break-all">{v.referrer || "-"}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">User</dt><dd className="text-[#F2F2F2]">{v.user_email ? `${v.user_name || ""} <${v.user_email}>` : "Anonymous"}</dd></div>
            <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Browser / OS / Device</dt><dd className="text-[#F2F2F2]">{browser} / {os} / {device}</dd></div>
            {v.page_views != null && <div><dt className="text-[#A1A1A1] uppercase tracking-wider">Page views in window</dt><dd className="text-[#F2F2F2]">{v.page_views}</dd></div>}
            <div className="md:col-span-2"><dt className="text-[#A1A1A1] uppercase tracking-wider">User Agent</dt><dd className="text-[#F2F2F2] break-all text-[10px]">{v.user_agent || "-"}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
}

export default function AdminVisitorsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState([]);
  const [stats, setStats] = useState(null);
  const [log, setLog] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(0);
  const [pathFilter, setPathFilter] = useState("");
  const [visitorFilter, setVisitorFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const pollRef = useRef(null);

  const fetchActive = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/visitors/active?minutes=${LIVE_WINDOW_MIN}`);
      setActive(data.visitors || []);
    } catch (e) {
      console.error("Failed to load active visitors", e);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/visitors/stats`);
      setStats(data);
    } catch (e) {
      console.error("Failed to load visitor stats", e);
    }
  }, []);

  const fetchLog = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), skip: String(page * PAGE_SIZE) });
      if (pathFilter.trim()) params.set("path_contains", pathFilter.trim());
      if (visitorFilter.trim()) params.set("visitor_id", visitorFilter.trim());
      const { data } = await axios.get(`${API}/api/admin/visitors?${params.toString()}`);
      setLog({ rows: data.rows || [], total: data.total || 0 });
    } catch (e) {
      console.error("Failed to load visitor log", e);
    } finally {
      setLoading(false);
    }
  }, [page, pathFilter, visitorFilter]);

  // Admin guard + initial load
  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchActive();
    fetchStats();
    fetchLog();
  }, [user, authLoading, navigate, fetchActive, fetchStats, fetchLog]);

  // Auto-refresh polling for live view + stats
  useEffect(() => {
    if (!autoRefresh || !user || user.role !== "admin") return;
    pollRef.current = setInterval(() => {
      fetchActive();
      fetchStats();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [autoRefresh, user, fetchActive, fetchStats]);

  const handleFilter = (e) => {
    e.preventDefault();
    setPage(0);
    fetchLog();
  };

  const totalPages = Math.max(1, Math.ceil(log.total / PAGE_SIZE));

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <button onClick={() => navigate("/admin")} className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3" data-testid="back-to-admin">
              <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
            </button>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="visitors-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Site Visitors
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[#A1A1A1] font-mono text-xs uppercase tracking-wider cursor-pointer" data-testid="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="accent-[#39FF14]"
              />
              Auto-refresh (5s)
            </label>
            <Button
              onClick={() => { fetchActive(); fetchStats(); fetchLog(); }}
              variant="outline"
              className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider"
              data-testid="manual-refresh-btn"
            >
              <RefreshCw className="h-3 w-3 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Stats tiles */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-[1px] bg-[#1A1A1A] mb-6" data-testid="visitor-stats">
            <StatTile label="Live (5 min)" value={active.length} sub="active visitors" icon={Eye} testid="stat-live" />
            <StatTile label="Unique (1 hr)" value={stats.unique_visitors.last_hour} sub={`${stats.pings.last_hour} page views`} icon={Users} testid="stat-unique-1h" />
            <StatTile label="Unique (24 hr)" value={stats.unique_visitors.last_24h} sub={`${stats.pings.last_24h} page views`} icon={TrendingUp} testid="stat-unique-24h" />
            <StatTile label="Top Path (24 hr)" value={stats.top_paths[0]?.views ?? 0} sub={stats.top_paths[0]?.path || "No data"} icon={Clock} testid="stat-top-path" />
          </div>
        )}

        {/* Top paths */}
        {stats && stats.top_paths.length > 0 && (
          <div className="bg-[#0D0D0D] p-5 mb-8 border border-[#1A1A1A]" data-testid="top-paths-panel">
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] font-bold text-[#A1A1A1] mb-3">Top Paths (Last 24h)</p>
            <ul className="space-y-1.5">
              {stats.top_paths.map((p, idx) => (
                <li key={p.path} className="flex items-center justify-between text-sm" data-testid={`top-path-${idx}`}>
                  <span className="text-[#F2F2F2] truncate flex-1 mr-3">
                    <span className="font-mono text-[#39FF14] mr-2">#{idx + 1}</span>
                    {p.path}
                  </span>
                  <span className="font-mono text-[#A1A1A1] text-xs">
                    <span className="text-[#39FF14] font-bold">{p.views}</span> views / {p.unique_visitors} unique
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Live Visitors */}
        <div className="mb-10" data-testid="live-visitors-section">
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-block w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
            <h2 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14]">Live Visitors — Last {LIVE_WINDOW_MIN} min</h2>
          </div>
          <div className="bg-[#0D0D0D] border border-[#1A1A1A]">
            {active.length === 0 ? (
              <p className="p-6 text-[#A1A1A1] text-sm font-mono text-center" data-testid="live-empty">No active visitors right now.</p>
            ) : (
              active.map((v) => (
                <VisitorRow
                  key={v.id}
                  v={v}
                  expanded={expandedId === `live-${v.id}`}
                  onToggle={() => setExpandedId(expandedId === `live-${v.id}` ? null : `live-${v.id}`)}
                  showLiveBadge
                />
              ))
            )}
          </div>
        </div>

        {/* Filter + Log */}
        <div data-testid="visitor-log-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-mono uppercase tracking-[0.2em] text-xs font-bold text-[#39FF14]">Visitor Log</h2>
            <span className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">
              {log.total} total pings
            </span>
          </div>

          <form onSubmit={handleFilter} className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input
              value={pathFilter}
              onChange={(e) => setPathFilter(e.target.value)}
              placeholder="Filter by path (e.g. /products)"
              data-testid="filter-path"
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono text-xs h-9"
            />
            <Input
              value={visitorFilter}
              onChange={(e) => setVisitorFilter(e.target.value)}
              placeholder="Filter by visitor_id"
              data-testid="filter-visitor"
              className="bg-[#0D0D0D] border-[#1A1A1A] text-[#F2F2F2] rounded-none focus:border-[#39FF14] font-mono text-xs h-9"
            />
            <Button type="submit" data-testid="filter-apply" className="bg-[#39FF14] text-[#050505] hover:bg-[#4DFF2E] rounded-none font-mono uppercase tracking-wider text-xs font-bold h-9 px-6">
              Apply
            </Button>
            {(pathFilter || visitorFilter) && (
              <Button
                type="button"
                onClick={() => { setPathFilter(""); setVisitorFilter(""); setPage(0); setTimeout(fetchLog, 0); }}
                variant="outline"
                className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider h-9"
                data-testid="filter-clear"
              >
                Clear
              </Button>
            )}
          </form>

          <div className="bg-[#0D0D0D] border border-[#1A1A1A]">
            {log.rows.length === 0 ? (
              <p className="p-6 text-[#A1A1A1] text-sm font-mono text-center" data-testid="log-empty">No visitor records.</p>
            ) : (
              log.rows.map((v) => (
                <VisitorRow
                  key={v.id}
                  v={v}
                  expanded={expandedId === `log-${v.id}`}
                  onToggle={() => setExpandedId(expandedId === `log-${v.id}` ? null : `log-${v.id}`)}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {log.total > PAGE_SIZE && (
            <div className="flex items-center justify-between mt-4" data-testid="pagination">
              <Button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                variant="outline"
                className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider disabled:opacity-30"
                data-testid="page-prev"
              >
                <ChevronLeft className="h-3 w-3 mr-1" /> Prev
              </Button>
              <span className="font-mono text-xs text-[#A1A1A1]">
                Page {page + 1} / {totalPages}
              </span>
              <Button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                variant="outline"
                className="bg-transparent border-[#1A1A1A] text-[#A1A1A1] hover:text-[#39FF14] hover:border-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider disabled:opacity-30"
                data-testid="page-next"
              >
                Next <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
